/**
 * Mission plans, and the record of what is actually on an aircraft.
 *
 * These are two different things and the API keeps them apart, so this module
 * does too. A plan belongs to a person and is a draft: /api/plans. A mission is
 * what an airframe holds, there is exactly one per drone, and the server only
 * writes it after the vehicle acknowledges the transfer: /api/missions.
 *
 * The transfer calls themselves (upload, download, start) live in api/drones.ts
 * because they are commands against a vehicle and share its result handling.
 * They are re-exported here so a planning screen has one import.
 */

import { api, type ApiResult } from '@/api/client';
// Type-only, and deliberately so: missionStore imports this module, and a value
// import here would close the cycle. `import type` is erased at build.
import type { Zone } from '@/stores/missionStore';
import type { DroneId, MissionPlan, Waypoint } from '@/types/domain';

export {
  downloadMission,
  getUploadedMission,
  startMission,
  pauseMission,
  resumeMission,
  uploadMission,
  uploadPlanMission,
  type MissionTransfer,
} from '@/api/drones';

export function listPlans(): Promise<ApiResult<{ plans: MissionPlan[] }>> {
  return api.get<{ plans: MissionPlan[] }>('/plans', { logged: false });
}

/**
 * The plan this operator is working in.
 *
 * The server creates one on first call rather than returning nothing, and it
 * never adopts a stranger's plan to do it. A tactical user sees every plan, so
 * "the first in the list" belongs to somebody else.
 */
export function getActivePlan(): Promise<ApiResult<MissionPlan>> {
  return api.get<MissionPlan>('/plans/active', { logged: false });
}

export function setActivePlan(planId: string | null): Promise<ApiResult<{ planId: string | null }>> {
  return api.put<{ planId: string | null }>('/plans/active', { planId }, { label: 'switch plan' });
}

export function getPlan(planId: string): Promise<ApiResult<MissionPlan>> {
  return api.get<MissionPlan>(`/plans/${encodeURIComponent(planId)}`, { logged: false });
}

export function createPlan(name: string): Promise<ApiResult<MissionPlan>> {
  return api.post<MissionPlan>('/plans', { name }, { label: `create plan "${name}"` });
}

/** Replace the routes in a plan. The whole map is sent, so a cleared drone clears. */
export function savePlanMissions(
  planId: string,
  missions: Record<DroneId, Waypoint[]>,
): Promise<ApiResult<MissionPlan>> {
  return api.put<MissionPlan>(`/plans/${encodeURIComponent(planId)}`, { missions }, {
    label: 'save plan',
  });
}

export function updatePlanMeta(
  planId: string,
  changes: { name?: string; collaboratorIds?: string[] },
): Promise<ApiResult<MissionPlan>> {
  return api.patch<MissionPlan>(`/plans/${encodeURIComponent(planId)}`, changes, {
    label: 'update plan',
  });
}

export function deletePlan(planId: string): Promise<ApiResult<{ ok: boolean }>> {
  return api.delete<{ ok: boolean }>(`/plans/${encodeURIComponent(planId)}`);
}

export function joinPlan(planId: string): Promise<ApiResult<MissionPlan>> {
  return api.post<MissionPlan>(
    `/plans/${encodeURIComponent(planId)}/collaborators/me`,
    undefined,
    { label: 'join plan' },
  );
}

export function leavePlan(planId: string): Promise<ApiResult<{ ok: boolean }>> {
  return api.delete<{ ok: boolean }>(`/plans/${encodeURIComponent(planId)}/collaborators/me`);
}

/** Every drone's uploaded mission in one call, keyed by drone id. */
export function listUploadedMissions(): Promise<
  ApiResult<{ missions: Record<DroneId, Waypoint[]> }>
> {
  return api.get<{ missions: Record<DroneId, Waypoint[]> }>('/missions', { logged: false });
}

/**
 * Forget the server's record of a drone's mission.
 *
 * This does not clear the aircraft. The endpoint says so and so does this
 * comment, because an operator who reads "clear mission" and expects the
 * airframe to be empty will be wrong at the worst moment.
 */
export function clearUploadedMissionRecord(droneId: DroneId): Promise<ApiResult<{ ok: boolean }>> {
  return api.delete<{ ok: boolean }>(`/missions/${encodeURIComponent(droneId)}`);
}

export interface ParsedMissionFile {
  waypoints: Waypoint[];
  sourceFormat: string;
  warnings: string[];
}

/** Turn an uploaded .waypoints or .plan file into waypoints without touching a vehicle. */
export function parseMissionFile(
  filename: string,
  content: string,
): Promise<ApiResult<ParsedMissionFile>> {
  return api.post<ParsedMissionFile>(
    '/missions/parse',
    { filename, content },
    { label: `parse ${filename}` },
  );
}

/** The server renders the file format; the response body is the file text. */
export function exportMission(
  waypoints: Waypoint[],
  format: 'waypoints' | 'plan',
): Promise<ApiResult<string>> {
  return api.post<string>('/missions/export', { waypoints, format }, { label: 'export mission' });
}

/**
 * Operating zones, shared across the deployment.
 *
 * Bulk replace rather than per-zone writes: the client holds the whole list, so
 * merging a partial update is how one stale client resurrects a keep-out
 * somebody else deleted.
 */
export function listZones(): Promise<ApiResult<{ zones: Zone[]; etag: string }>> {
  return api.get<{ zones: Zone[]; etag: string }>('/zones', { logged: false });
}

/**
 * `ifMatch` is the etag the list was read at. Given, the server answers 409
 * instead of writing when somebody else saved in the meantime, so a list held
 * through a drag cannot delete a zone drawn while the mouse was down.
 */
export function saveZones(
  zones: Zone[],
  ifMatch?: string,
): Promise<ApiResult<{ zones: Zone[]; etag: string }>> {
  return api.put<{ zones: Zone[]; etag: string }>(
    '/zones',
    ifMatch ? { zones, ifMatch } : { zones },
    { label: 'ZONES SAVE' },
  );
}
